/**
 * Input sanitization utilities for security and data integrity.
 * Prevents XSS, injection, oversized payloads, and invalid data.
 */

/** Allowed mood IDs - single source of truth for validation */
const MOOD_IDS = ["ecstatic", "happy", "neutral", "sad", "angry"];

/** Strip control chars and null bytes (no trim - preserves spaces for typing) */
function stripUnsafeChars(str) {
  if (str == null || typeof str !== "string") return "";
  return str
    .replace(/\0/g, "")
    // Control chars intentionally stripped for security
    // eslint-disable-next-line no-control-regex -- intentional for XSS prevention
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
}

/**
 * Sanitize free-form text (journal entry, summary).
 * Strips unsafe chars and enforces max length.
 * Does not trim - allows spaces between words while typing.
 */
export function sanitizeText(str, maxLen = 50000) {
  const cleaned = stripUnsafeChars(String(str ?? ""));
  return cleaned.length > maxLen ? cleaned.slice(0, maxLen) : cleaned;
}

/**
 * Sanitize short text (habit name, journal name, username, display name).
 * Trims leading/trailing whitespace for these bounded inputs.
 */
export function sanitizeShortText(str, maxLen = 100) {
  const cleaned = stripUnsafeChars(String(str ?? "")).trim();
  return cleaned.length > maxLen ? cleaned.slice(0, maxLen) : cleaned;
}

/**
 * Validate and normalise a journal entry location tag.
 * Returns null when the value is missing or malformed.
 * Shape: { name, city?, country, countryCode (ISO alpha-2), lat, lng }
 */
export function sanitizeLocation(loc) {
  if (!loc || typeof loc !== "object") return null;
  const lat = Number(loc.lat);
  const lng = Number(loc.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  const countryCode = String(loc.countryCode ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(countryCode)) return null;
  const name = sanitizeShortText(loc.name, 200);
  const country = sanitizeShortText(loc.country, 100);
  const city = sanitizeShortText(loc.city, 100);
  if (!name && !country) return null;
  const out = {
    name: name || country,
    country: country || name,
    countryCode,
    lat: Math.round(lat * 1e5) / 1e5,
    lng: Math.round(lng * 1e5) / 1e5,
  };
  if (city) out.city = city;
  return out;
}

/**
 * Sanitize search query - strip SQL-like chars and limit length.
 */
export function sanitizeSearch(query, maxLen = 100) {
  const cleaned = stripUnsafeChars(String(query ?? "")).trim()
    .replace(/[%_\\]/g, "");
  return cleaned.length > maxLen ? cleaned.slice(0, maxLen) : cleaned;
}

/**
 * Validate mood against allowed IDs.
 */
export function sanitizeMood(mood) {
  if (mood == null || mood === "") return undefined;
  const id = String(mood).toLowerCase().trim();
  return MOOD_IDS.includes(id) ? id : undefined;
}

/**
 * Sanitize sleep hours (0-24).
 */
export function sanitizeSleep(value) {
  if (value === "" || value == null) return undefined;
  const n = parseFloat(String(value).replace(/,/g, ""));
  if (Number.isNaN(n)) return undefined;
  if (n < 0 || n > 24) return undefined;
  return Math.round(n * 100) / 100; // 2 decimal places
}

/**
 * Sanitize weight in lbs (0-1200). Caller converts kg if needed.
 * 1200 lbs allows ~545 kg display.
 */
export function sanitizeWeight(value) {
  if (value === "" || value == null) return undefined;
  const n = parseFloat(String(value).replace(/,/g, ""));
  if (Number.isNaN(n)) return undefined;
  if (n <= 0 || n > 1200) return undefined;
  return Math.round(n * 10) / 10;
}

/**
 * Sanitize year (1900-2100).
 */
export function sanitizeYear(value) {
  const n = parseInt(String(value).replace(/\D/g, ""), 10);
  if (Number.isNaN(n)) return null;
  if (n < 1900 || n > 2100) return null;
  return n;
}

/**
 * Habit card background: null clears; hex #rgb or #rrggbb only.
 * Returns undefined if value is present but invalid.
 */
export function sanitizeHabitCardColor(color) {
  if (color == null || color === "") return null;
  const s = String(color).trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return undefined;
}

/**
 * Sanitize brush size (1-50).
 */
export function sanitizeBrushSize(value) {
  const n = parseInt(String(value), 10);
  if (Number.isNaN(n)) return 3;
  return Math.max(1, Math.min(50, n));
}

/**
 * Sanitize drawing data. Must be a data URL (image/png or image/jpeg) under max bytes.
 */
const DATA_URL_REGEX = /^data:image\/(png|jpeg|jpg);base64,[A-Za-z0-9+/=]+$/;
const MAX_DRAWING_BYTES = 512 * 1024;

export function sanitizeDrawing(drawing, maxBytes = MAX_DRAWING_BYTES) {
  if (drawing == null || drawing === "") return null;
  const str = String(drawing);
  if (str.length > maxBytes) return null;
  if (!DATA_URL_REGEX.test(str)) return null;
  return str;
}

/**
 * Basic email validation (format only; Supabase handles auth).
 */
export function sanitizeEmail(email) {
  const trimmed = String(email ?? "").trim().toLowerCase();
  // Simple format check - RFC-compliant validation is complex
  const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return basicEmailRegex.test(trimmed) && trimmed.length <= 254 ? trimmed : "";
}

/**
 * Delete confirmation - must exactly match (case-insensitive).
 */
export function sanitizeDeleteConfirmation(input, expected = "i understand") {
  return String(input ?? "").trim().toLowerCase() === expected.toLowerCase();
}
